import { type Cascade, cascadeNodesAt, type FieldConfig, parseFieldConfig } from './fieldConfig';
import { DEFAULT_ADDRESS_SUBFIELDS } from './registry';

/**
 * What an applicant's answers look like, and what counts as a valid one.
 *
 * SHARED BY THE BUILDER PREVIEW AND THE LIVE RUN. The preview is a throwaway copy of the real
 * thing — same renderer, same answer shape, same validation — so an admin trying their own form
 * in the preview hits exactly the errors an applicant would. The alternative (a preview that only
 * *looks* like the form) is how you ship a mandatory field nobody notices is unfillable.
 */

/**
 * One field, flattened to what the renderer needs.
 *
 * Deliberately NOT `BuilderField` and NOT `QuestionnaireFieldDto`: the builder identifies fields
 * by a client-side `key` and the run identifies them by a server `id`, and the renderer should not
 * have to know which world it is in. Both map into this.
 */
export interface RenderableField {
  /** Stable identity, and the key this field's answer is stored under. */
  id: string;
  fieldType: string;
  label: string;
  placeholder: string;
  validationMessage: string;
  isMandatory: boolean;
  config: FieldConfig;
}

export interface RenderableSection {
  /** Stable identity, and the key this section's answers are stored under. */
  id: string;
  /** Already resolved — the caller decides whether that is the title or the tab name. */
  title: string;
  description: string;
  fields: RenderableField[];
}

/** An uploaded file, held as a data URI so it survives a page's lifetime without a server round-trip. */
export interface UploadedFile {
  name: string;
  /** `data:<mime>;base64,<...>` — offloaded to object storage on submit. */
  dataUrl: string;
}

export interface DateRangeAnswer {
  from: string;
  to: string;
}

/**
 * Answers for one section, keyed by field id.
 *
 * `unknown` rather than a union because the shape is decided by `fieldType`, which is an open
 * string — a closed union here would have to be widened for every new element type, and the
 * accessors below already narrow safely at each use.
 */
export type SectionAnswers = Record<string, unknown>;

/** All sections' answers, keyed by section id. */
export type QuestionnaireAnswers = Record<string, SectionAnswers>;

// ── Reading answers ───────────────────────────────────────────────────────────
// Every accessor tolerates a missing or wrong-typed value and returns the empty case. Answers
// come back from the server as JSON that a previous build wrote, so "the wrong type is in there"
// is a real state, not a defensive hypothetical.

export function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * A SINGLE choice — one value.
 *
 * Stored as a plain string, like every other pick-one control (drop-down, state, city). It used to be
 * written as a one-element ARRAY (`set([option])`) while every reader treated it as text, which had two
 * consequences: the session view showed "Not answered" for a question that had been answered, and
 * `isBlank` saw an empty string — so a MANDATORY single-choice could never be satisfied and the
 * applicant was stuck on the section forever.
 *
 * Still accepts the array shape, because sessions answered by that build have it in their stored JSON
 * and those answers are a record of what the applicant chose. Reading them is not optional.
 */
export function asSingleChoice(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const first = value.find((v): v is string => typeof v === 'string');
    return first ?? '';
  }
  return '';
}

export function asChoices(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export function asBool(value: unknown): boolean {
  return value === true;
}

export function asDateRange(value: unknown): DateRangeAnswer {
  if (value && typeof value === 'object') {
    const v = value as Partial<DateRangeAnswer>;
    return { from: asText(v.from), to: asText(v.to) };
  }
  return { from: '', to: '' };
}

export function asAddress(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === 'string') out[key] = v;
  }
  return out;
}

export function asUpload(value: unknown): UploadedFile | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<UploadedFile>;
  return typeof v.name === 'string' && typeof v.dataUrl === 'string'
    ? { name: v.name, dataUrl: v.dataUrl }
    : null;
}

/**
 * Every file attached to one Upload field.
 *
 * An Upload field now holds an ARRAY. It used to hold a single `{ name, dataUrl }` object, and this
 * reads BOTH: a stored object comes back as a one-element array. That tolerance is not defensive
 * padding — sessions submitted before the change are on record and the admin's answer view has to
 * keep rendering them, and a step's stored input is an audit record that is never rewritten.
 */
export function asUploads(value: unknown): UploadedFile[] {
  if (Array.isArray(value)) {
    return value.map(asUpload).filter((f): f is UploadedFile => f !== null);
  }

  const single = asUpload(value);
  return single ? [single] : [];
}

/**
 * Decoded size of a data URI, in bytes, derived from the base64 length.
 *
 * Base64 carries 3 bytes in every 4 characters, and the one or two `=` at the end are padding for
 * bytes that are not there — so they are subtracted rather than counted. Exact for well-formed
 * input, which is all we produce (FileReader), and never used for anything but a size check.
 *
 * Computed rather than stored: the file's own `size` would have to be persisted alongside it, and a
 * number the client supplies is not something a size limit can rely on anyway.
 */
export function uploadByteSize(file: UploadedFile): number {
  const comma = file.dataUrl.indexOf(',');
  if (comma < 0) return 0;

  const payload = file.dataUrl.slice(comma + 1);
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.max(Math.floor((payload.length * 3) / 4) - padding, 0);
}

/**
 * Total bytes attached across EVERY upload field of every section.
 *
 * The budget is per STEP, not per field, because that is what the transport actually constrains: a
 * questionnaire step submits all of its sections in ONE request, so it is the sum that has to fit —
 * see SessionUploadLimits on the server.
 */
export function totalUploadBytes(answers: QuestionnaireAnswers): number {
  let total = 0;
  for (const section of Object.values(answers)) {
    for (const value of Object.values(section)) {
      for (const file of asUploads(value)) {
        total += uploadByteSize(file);
      }
    }
  }
  return total;
}

/**
 * The address lines a field actually has, falling back to the standard three.
 *
 * One function rather than the fallback being repeated in the config panel, the renderer and the
 * answer summary — which is where the reference has it, and why its three copies of the default
 * list had already drifted apart in wording.
 */
export function addressSubfieldsOf(config: FieldConfig) {
  const stored = config.addressSubfields;
  return stored && stored.length > 0 ? stored : DEFAULT_ADDRESS_SUBFIELDS.map((s) => ({ ...s }));
}

/** A field's option list, or an empty array. */
export function optionsOf(config: FieldConfig): string[] {
  return config.options ?? [];
}

// ── Cascade answers ───────────────────────────────────────────────────────────

/**
 * One tier of a chosen cascade path.
 *
 * CARRIES BOTH THE ID AND THE LABEL, and that is the whole point. The id is the stable machine
 * value, so reporting and matching survive an admin relabelling an option. The label is what the
 * applicant actually read at the moment they answered — the audit truth, which an id alone cannot
 * give you: reading `"n_4f2a"` out of a KYC record a year later, with the questionnaire since
 * edited, tells you nothing about what was on their screen.
 *
 * `level` is captured for the same reason: "District: Ahmedabad" has to stay legible after the
 * tier has been renamed.
 */
export interface CascadeStep {
  levelId: string;
  /** The tier's title as shown, e.g. "District". */
  level: string;
  id: string;
  label: string;
}

/**
 * A cascade answer: the ordered chain the applicant picked.
 *
 * An ARRAY, so depth is data. Two tiers or five, the stored shape is identical — which is what lets
 * a webhook consumer or an auditor read the answer without knowing how the questionnaire was built.
 * The rejected alternative was an object keyed by level title (`{"country":…,"state":…}`): titles
 * are free text an admin can rename or duplicate, so they cannot be keys.
 */
export interface CascadeAnswer {
  path: CascadeStep[];
}

export function asCascade(value: unknown): CascadeAnswer {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { path: [] };
  }
  const raw = (value as { path?: unknown }).path;
  if (!Array.isArray(raw)) {
    return { path: [] };
  }

  const path: CascadeStep[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const step = entry as Partial<CascadeStep>;
    if (typeof step.id !== 'string' || typeof step.label !== 'string') continue;
    path.push({
      levelId: typeof step.levelId === 'string' ? step.levelId : '',
      level: typeof step.level === 'string' ? step.level : '',
      id: step.id,
      label: step.label,
    });
  }
  return { path };
}

/** Just the ids, for walking the tree. */
export function cascadePathIds(answer: CascadeAnswer): string[] {
  return answer.path.map((step) => step.id);
}

/**
 * How many tiers the applicant can actually reach, given what they have picked so far.
 *
 * NOT simply `levels.length`. A branch may be shallower than the deepest one — an admin can give
 * India three tiers of options and leave a second country with only one — so requiring every
 * configured level would make a legitimately short branch impossible to complete. This counts the
 * tiers that have options to offer.
 */
export function reachableCascadeDepth(cascade: Cascade, chosen: readonly string[]): number {
  let depth = 0;
  const path: string[] = [];

  for (let i = 0; i < cascade.levels.length; i++) {
    if (cascadeNodesAt(cascade, path).length === 0) break;
    depth = i + 1;
    const next = chosen[i];
    if (next === undefined) break;
    path.push(next);
  }

  return depth;
}

// ── Validation ────────────────────────────────────────────────────────────────

/** Field id → message. Empty when the section is complete. */
export type SectionErrors = Record<string, string>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Indian mobile: 10 digits starting 6–9, the same rule the sessions list formats against. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

/**
 * Whether a field has been answered at all.
 *
 * Per-type because "empty" is not one thing: an unticked consent box is `false`, an untouched
 * multi-select is `[]`, and a partly-filled address is neither empty nor complete.
 */
function isBlank(field: RenderableField, value: unknown): boolean {
  // A cascade is incomplete, not just empty, until every REACHABLE tier is chosen.
  //
  // THIS CLOSES A REAL HOLE. When the dependent list was a separate `mappedDropdowns` entry its
  // answer lived under a synthetic key that was not a field, so `validateSection` — which walks
  // `section.fields` — never saw it. An applicant could pick "Gujarat", leave City blank, and submit
  // a step marked Mandatory. Making the whole chain one field's answer is what makes it checkable.
  if (field.fieldType === 'drop-down' && field.config.cascade) {
    const answer = asCascade(value);
    const required = reachableCascadeDepth(field.config.cascade, cascadePathIds(answer));
    return answer.path.length < required;
  }

  switch (field.fieldType) {
    case 'consent-text':
      return !asBool(value);
    case 'single-choice':
      return asSingleChoice(value).trim().length === 0;
    case 'multiple-choice':
      return asChoices(value).length === 0;
    case 'date-range': {
      const { from, to } = asDateRange(value);
      return from.trim().length === 0 || to.trim().length === 0;
    }
    case 'address': {
      const answer = asAddress(value);
      // EVERY line must be filled, not just one. A mandatory address with only a flat number is
      // not an address, and accepting it would put an unusable value into a KYC record.
      return addressSubfieldsOf(field.config).some(
        (sub) => (answer[sub.id] ?? '').trim().length === 0,
      );
    }
    case 'upload':
      // At least ONE file. `asUploads` reads the array shape and the legacy single object, so a
      // mandatory upload answered before the multi-file change still counts as answered.
      return asUploads(value).length === 0;
    default:
      return asText(value).trim().length === 0;
  }
}

/**
 * A format complaint for an answered field, or null.
 *
 * Only runs on non-blank values, so an optional field left alone is never format-checked.
 * `validationMessage` wins when the admin wrote one — they know their applicants — and the
 * built-in wording is the fallback.
 */
function formatError(field: RenderableField, value: unknown): string | null {
  const text = asText(value).trim();
  if (text.length === 0) {
    return null;
  }

  switch (field.fieldType) {
    case 'email':
      return EMAIL_PATTERN.test(text) ? null : 'Enter a valid email address.';
    case 'mobile-number': {
      // Tolerant of what people actually type — +91, 0 prefixes, spaces and dashes — because
      // rejecting "+91 98765 43210" as malformed would be rejecting a correct number.
      const digits = text
        .replace(/[\s-]/g, '')
        .replace(/^\+?91/, '')
        .replace(/^0/, '');
      return MOBILE_PATTERN.test(digits) ? null : 'Enter a valid 10-digit mobile number.';
    }
    case 'number':
      return Number.isFinite(Number(text)) ? null : 'Enter a valid number.';
    case 'date-range': {
      const { from, to } = asDateRange(value);
      if (from.length > 0 && to.length > 0 && from > to) {
        return 'The end date cannot be before the start date.';
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Everything wrong with one section's answers.
 *
 * Returns ALL errors at once rather than stopping at the first: an applicant on a phone should
 * see every field they need to fix in one pass, not be walked down the form one error at a time.
 */
export function validateSection(
  section: RenderableSection,
  answers: SectionAnswers,
): SectionErrors {
  const errors: SectionErrors = {};

  for (const field of section.fields) {
    const value = answers[field.id];

    if (field.isMandatory && isBlank(field, value)) {
      errors[field.id] =
        field.validationMessage.trim().length > 0
          ? field.validationMessage.trim()
          : defaultRequiredMessage(field.fieldType);
      continue;
    }

    const format = formatError(field, value);
    if (format) {
      // An admin's own message describes their rule, which is more specific than "not a valid
      // email", so it wins here too.
      errors[field.id] =
        field.validationMessage.trim().length > 0 ? field.validationMessage.trim() : format;
    }
  }

  return errors;
}

/**
 * Wording for a missing answer when the admin left the message blank.
 *
 * Per-type because "This field is required" is wrong for half of them — a consent checkbox is not
 * a field, and telling someone to "fill in" a document upload describes the wrong action.
 */
function defaultRequiredMessage(fieldType: string): string {
  switch (fieldType) {
    case 'consent-text':
      return 'Please tick this to continue.';
    case 'upload':
      return 'Please upload a document.';
    case 'single-choice':
    case 'state':
    case 'district':
    case 'city':
      return 'Please choose an option.';
    // Covers the cascade too: "choose an option" would be wrong for a half-finished chain, where
    // the applicant HAS chosen — just not all the way down.
    case 'drop-down':
      return 'Please complete every selection.';
    case 'multiple-choice':
      return 'Please choose at least one option.';
    case 'date':
      return 'Please choose a date.';
    case 'date-range':
      return 'Please choose both dates.';
    case 'address':
      return 'Please complete every line of the address.';
    default:
      return 'This field is required.';
  }
}

// ── Mapping into the renderer's shape ────────────────────────────────────────

/**
 * A stored field DTO as something renderable.
 *
 * Kept here rather than in the runner so the builder preview and the live run cannot disagree
 * about what a stored field means.
 */
export function toRenderableField(dto: {
  id: string;
  fieldType: string;
  label: string;
  placeholder: string | null;
  validationMessage?: string | null;
  configJson?: string | null;
  isMandatory: boolean;
}): RenderableField {
  return {
    id: dto.id,
    fieldType: dto.fieldType,
    label: dto.label,
    placeholder: dto.placeholder ?? '',
    validationMessage: dto.validationMessage ?? '',
    isMandatory: dto.isMandatory,
    config: parseFieldConfig(dto.configJson),
  };
}
